import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { loadErrorMessage } from "shared/loadOrError";
import { isAdmin } from "auth/tier";
import { canUseDataMapper } from "../rbac";
import { dataMapperSelection, dataMapperService, dataPlaneService } from "../dataMapperServices";
import { subscribeTabEvents, type DataMapperTabEventPayload } from "../tabEvents";
import {
  NOT_CONNECTED_REASONS,
  refusalReason,
  sameId,
  type DataMapperProfile,
  type DataMapperScope,
} from "../dataMapper.types";
import {
  ANALYSIS_EVENTS,
  DM_EVENTS,
  DM_EXPLORER_ANALYSIS_PATH,
  DM_EXPLORER_PROFILER_PATH,
  IDLE_JOB,
  PROFILER_EVENTS,
  type AnalysisEvent,
  type DataQualityAssessment,
  type DatabaseAnalysis,
  type ExplorerReport,
  type ExplorerReportListing,
  type ExplorerReportMeta,
  type ExplorerTableSchema,
  type InferredRelationship,
  type JobProgress,
  type JobState,
  type JobStatusAnswer,
  type ProfilerEvent,
  type TableClassification,
  type TableProfile,
} from "./explorer.types";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/** The Data Source route, where a tab with no open source is sent. */
export const DATA_MAPPER_SOURCE_ROUTE = "Data Mapper Source";
/** HQ's LLM task routing page, where an admin wires the `data-mapper.*` tasks. */
export const LLM_ROUTING_ROUTE = "LlmRouting";

/** Said in place of an analysis when HQ's LLM router is not wired for the Data Mapper. */
export const LLM_UNAVAILABLE_MESSAGE =
  "AI analysis is not available: HQ's LLM router is not set up for the Data Mapper here. " +
  "An administrator can route the data-mapper tasks (classify table, assess quality, infer relationships, " +
  "domain summary) to a provider under AI → Task Routing.";

/** The refusals a job start can meet, as sentences. */
const JOB_REFUSALS: Record<string, string> = {
  "job-running": "This tab is already profiling or analysing this database. Wait for it to finish.",
  "too-many-jobs": "You already have two explorer jobs running (in this or another tab). Wait for one to finish.",
};

const GONE_REASONS = new Set(["profile-not-found", "profile-not-in-client", "client-not-found"]);

/** At most this many byte-pages are followed for one list (schema, profiles, analyses). */
const MAX_PAGES = 50;

/** A report could not be saved or deleted because it changed meanwhile (409). */
export const REPORT_CONFLICT_MESSAGE =
  "The saved reports changed while you were working (someone else saved or deleted one). The list has been reloaded.";
/** The report asked for is no longer there (404). */
export const REPORT_GONE_MESSAGE = "That report has already been deleted. The list has been reloaded.";

const isCode = (err: unknown, code: number) => (err as { code?: unknown } | null)?.code === code;

/** A data-plane refusal as the sentence the explorer shows. */
export function explorerErrorMessage(err: unknown, fallback: string): string {
  const reason = refusalReason(err) ?? "";
  if (reason === "llm-unavailable") return LLM_UNAVAILABLE_MESSAGE;
  if (JOB_REFUSALS[reason]) return JOB_REFUSALS[reason];
  return loadErrorMessage(err, fallback);
}

interface PageAnswer<T> {
  rows?: T[];
  nextCursor?: number | null;
  truncated?: boolean;
  count?: number;
  total?: number;
  found?: boolean;
}

/**
 * Data Mapper → Explore: the open source's schema (tables, columns, keys),
 * column profiles, AI analysis and saved reports. Ported from the standalone
 * tool's Explorer dialog and ExplorerStore.
 *
 * WHAT IT HOLDS, AND WHY HERE. The schema, the profiles and the analyses are
 * the data plane's answers about a client's database for THIS tab — no HQ ids,
 * no place in a shared cache (byte-paged envelopes), and the server keeps them
 * per `user|tab` in memory anyway. They are held for the page's life the way
 * DataMapperBrowseStore holds a table's rows, and dropped on exit. The saved
 * reports are HQ rows, but `data-mapper-explorer-reports` answers `find` grouped
 * by database and `get` with the whole report: neither fits a ServiceStore's
 * entity cache, and the service emits no events. So they are read through the
 * raw client and held here for the page's life too.
 *
 * BACKGROUND JOBS. Profiling and the full analysis answer `{ started: true }`;
 * progress and the end arrive as events for this tab (tabEvents.ts). On entry
 * the screen also asks each job's `status`, so a reload mid-job resumes it.
 */
class DataMapperExplorerStore {
  rootStore: RootStore;

  // ---- Schema -------------------------------------------------------------------
  tables: ExplorerTableSchema[] = [];
  tablesLoading = false;
  tablesError: string | null = null;
  /** The list stopped at the page ceiling: more tables exist than were read. */
  tablesCut = false;
  /** The tab has no source open for this selection: the page says where to connect. */
  notConnected = false;
  /** The open source's name (the data-source status answer). */
  sourceName: string | null = null;
  tableSearch = "";
  selectedTable: string | null = null;

  // ---- Profiles -----------------------------------------------------------------
  profiles: Record<string, TableProfile> = {};
  profileJob: JobState = { ...IDLE_JOB };

  // ---- Analysis -----------------------------------------------------------------
  classifications: Record<string, TableClassification> = {};
  qualities: Record<string, DataQualityAssessment> = {};
  relationships: InferredRelationship[] | null = null;
  fullAnalysis: DatabaseAnalysis | null = null;
  analysisJob: JobState = { ...IDLE_JOB };
  /** A one-shot analysis call in flight (they answer when done). */
  analysisBusy: "classify" | "quality" | "relationships" | null = null;
  analysisError: string | null = null;
  /** HQ's LLM router is not wired for the Data Mapper: every analysis action is refused. */
  llmUnavailable = false;

  // ---- Reports ------------------------------------------------------------------
  reports: ExplorerReportListing[] = [];
  reportsLoading = false;
  reportsError: string | null = null;
  reportNotice: string | null = null;
  reportBusy: "save" | "open" | "delete" | null = null;
  openedReport: ExplorerReport | null = null;
  reportScope: DataMapperScope = "profile";

  private disposeEvents: (() => void) | null = null;
  /** Bumped on exit and on a selection change: an answer that lands after either is dropped. */
  private epoch = 0;
  private loadSeq = 0;
  // One sequence per loader: a refresh re-enters the route without leaving it,
  // so only the latest call of each may write (Browse's pattern; Codex r1).
  private tablesSeq = 0;
  private profilesSeq = 0;
  private analysisSeq = 0;
  private reportsSeq = 0;
  /** Bumped by every profiler / analysis event: a status answer older than one is not applied. */
  private profilerEventSeq = 0;
  private analysisEventSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private get selection() {
    return dataMapperSelection(this.rootStore);
  }

  get canUse(): boolean {
    return canUseDataMapper(this.rootStore);
  }

  get isAdmin(): boolean {
    return isAdmin(this.rootStore);
  }

  get profileId(): string | null {
    return this.selection?.selectedProfileId ?? null;
  }

  get profile(): DataMapperProfile | null {
    const id = this.profileId;
    if (!id) return null;
    const all = (dataMapperService(this.rootStore, "data-mapper-profiles")?.entities ??
      []) as unknown as DataMapperProfile[];
    return all.find((p) => sameId(p.id, id)) ?? null;
  }

  get filteredTables(): ExplorerTableSchema[] {
    const q = this.tableSearch.trim().toLowerCase();
    return q ? this.tables.filter((t) => t.fullName.toLowerCase().includes(q)) : this.tables;
  }

  get selectedSchema(): ExplorerTableSchema | null {
    return this.tables.find((t) => t.fullName === this.selectedTable) ?? null;
  }

  get selectedProfile(): TableProfile | null {
    return this.selectedTable ? (this.profiles[this.selectedTable] ?? null) : null;
  }

  get selectedClassification(): TableClassification | null {
    if (!this.selectedTable) return null;
    return (
      this.classifications[this.selectedTable] ??
      this.fullAnalysis?.tables.find((t) => t.tableName === this.selectedTable)?.classification ??
      null
    );
  }

  get selectedQuality(): DataQualityAssessment | null {
    if (!this.selectedTable) return null;
    return (
      this.qualities[this.selectedTable] ??
      this.fullAnalysis?.tables.find((t) => t.tableName === this.selectedTable)?.qualityAssessment ??
      null
    );
  }

  get profiledCount(): number {
    return Object.keys(this.profiles).length;
  }

  get jobRunning(): boolean {
    return this.profileJob.status === "running" || this.analysisJob.status === "running";
  }

  /** Something worth saving: a profile or an analysis. */
  get canSaveReport(): boolean {
    return (this.profiledCount > 0 || this.fullAnalysis !== null) && this.reportBusy === null;
  }

  // ---- Route entry / exit -----------------------------------------------------------

  async enter(): Promise<void> {
    const authStore = (
      this.rootStore as unknown as { authStore?: { currentUser?: { id?: string | number } } }
    ).authStore;
    this.selection?.bindToUser(authStore?.currentUser?.id);
    if (!this.canUse || !this.selection?.hasProfile) return;
    this.subscribe();
    await this.load();
  }

  /** Listen for this tab's profiler and analysis events (once per visit). */
  private subscribe() {
    const selection = this.selection;
    if (this.disposeEvents || !selection) return;
    const offProfiler = subscribeTabEvents<ProfilerEvent>(this.rootStore, {
      path: DM_EXPLORER_PROFILER_PATH,
      events: PROFILER_EVENTS,
      tabId: selection.tabId,
      onEvent: (event, payload) => this.onProfilerEvent(event, payload),
    });
    const offAnalysis = subscribeTabEvents<AnalysisEvent>(this.rootStore, {
      path: DM_EXPLORER_ANALYSIS_PATH,
      events: ANALYSIS_EVENTS,
      tabId: selection.tabId,
      onEvent: (event, payload) => this.onAnalysisEvent(event, payload),
    });
    this.disposeEvents = () => {
      offProfiler();
      offAnalysis();
    };
  }

  /** Everything the page shows: the source's name, its schema, the jobs' state, the reports. */
  async load(): Promise<void> {
    const seq = ++this.loadSeq;
    const selection = this.selection;
    if (!this.canUse || !selection?.hasProfile) return;
    const query = { ...selection.dataPlaneQuery };
    const status = dataPlaneService(this.rootStore, "data-mapper-data-source");
    // The reset after a sign-out bumps loadSeq, but the epoch moves first: the
    // follow-up reads below must not start in the next person's session.
    const ended = sessionMark(this.rootStore);
    try {
      const answer = (await status?.create({ action: "status", ...query })) as {
        connected?: boolean;
        name?: string | null;
      } | null;
      if (seq !== this.loadSeq || ended()) return;
      runInAction(() => {
        this.notConnected = answer?.connected !== true;
        this.sourceName = answer?.connected ? (answer.name ?? null) : null;
      });
    } catch (err) {
      if (seq !== this.loadSeq || isSessionEnded(err) || ended()) return;
      runInAction(() => {
        if (GONE_REASONS.has(refusalReason(err) ?? "") && query.profileId) {
          selection.forgetProfile(query.profileId);
          this.tablesError = "The profile that was open no longer exists. Choose one on the Profiles page.";
        } else {
          this.tablesError = loadErrorMessage(err, "The source's status could not be read.");
        }
      });
      return;
    }
    await Promise.all([
      // So the header can name the profile after a reload (the Data Source page's load does the same).
      Promise.resolve()
        .then(() => dataMapperService(this.rootStore, "data-mapper-profiles")?.find({ query: { clientId: query.clientId } }))
        .catch(() => undefined),
      this.notConnected ? Promise.resolve() : this.loadTables(),
      this.notConnected ? Promise.resolve() : this.refreshJobs(),
      this.loadReports(),
    ]);
  }

  /** The source's tables with their columns, indexes and keys, following the byte pages. */
  async loadTables(): Promise<void> {
    const service = dataPlaneService(this.rootStore, "data-mapper-explorer-schema");
    const selection = this.selection;
    if (!service || !selection?.hasProfile) return;
    const query = { ...selection.dataPlaneQuery };
    const seq = ++this.tablesSeq;
    const ended = sessionMark(this.rootStore);
    const current = () => seq === this.tablesSeq && !ended();
    this.tablesLoading = true;
    this.tablesError = null;
    try {
      const all: ExplorerTableSchema[] = [];
      let skip: number | null = 0;
      for (let page = 0; skip !== null && page < MAX_PAGES; page++) {
        if (!current()) return;
        const answer = (await service.find({ query: { ...query, $skip: skip } })) as PageAnswer<ExplorerTableSchema>;
        all.push(...(answer?.rows ?? []));
        skip = typeof answer?.nextCursor === "number" && answer.nextCursor > skip ? answer.nextCursor : null;
      }
      const cut = skip !== null;
      runInAction(() => {
        if (!current()) return;
        this.tables = all;
        this.tablesCut = cut;
        this.notConnected = false;
        if (this.selectedTable && !all.some((t) => t.fullName === this.selectedTable)) this.selectedTable = null;
      });
    } catch (err) {
      if (isSessionEnded(err)) return;
      runInAction(() => {
        if (!current()) return;
        if (NOT_CONNECTED_REASONS.has(refusalReason(err) ?? "")) {
          this.notConnected = true;
          this.tables = [];
        } else {
          this.tablesError = loadErrorMessage(err, "The source's schema could not be read.");
        }
      });
    } finally {
      runInAction(() => {
        if (current()) this.tablesLoading = false;
      });
    }
  }

  setTableSearch(value: string) {
    this.tableSearch = value;
  }

  selectTable(fullName: string | null) {
    this.selectedTable = fullName;
  }

  // ---- Jobs: status on entry ---------------------------------------------------------

  /** Ask each job's status (a reload mid-job resumes it) and read whatever is already done. */
  async refreshJobs(): Promise<void> {
    const selection = this.selection;
    if (!selection?.hasProfile) return;
    const query = { ...selection.dataPlaneQuery };
    const epoch = this.epoch;
    const ended = sessionMark(this.rootStore);
    const live = () => epoch === this.epoch && !ended();
    // A status answer is applied only if no event for that job landed while it was
    // in flight: the event is newer (Codex r1).
    const profilerSeen = this.profilerEventSeq;
    const analysisSeen = this.analysisEventSeq;
    const profiler = dataPlaneService(this.rootStore, "data-mapper-explorer-profiler");
    const analysis = dataPlaneService(this.rootStore, "data-mapper-explorer-analysis");
    const [p, a] = await Promise.allSettled([
      profiler?.create({ action: "status", ...query }),
      analysis?.create({ action: "status", ...query }),
    ]);
    if (!live()) return;
    if (p.status === "fulfilled" && p.value && profilerSeen === this.profilerEventSeq) {
      const s = p.value as JobStatusAnswer;
      runInAction(() => {
        this.profileJob = this.jobFromStatus(s);
      });
      if ((s.profiledTables ?? 0) > 0) await this.loadProfiles();
      // The profile read is its own loader: it swallows a sign-out and the page
      // being left, and this method would then write the analysis status into
      // the emptied store and start the result read there.
      if (!live()) return;
    }
    if (a.status === "fulfilled" && a.value && analysisSeen === this.analysisEventSeq) {
      const s = a.value as JobStatusAnswer;
      runInAction(() => {
        this.analysisJob = this.jobFromStatus(s);
      });
      if (s.status === "completed" && s.hasResult) await this.loadAnalysisResult();
    }
  }

  private jobFromStatus(s: JobStatusAnswer): JobState {
    const status = s.status ?? "idle";
    return { status, kind: s.kind ?? null, progress: s.progress ?? null, error: s.error ?? null };
  }

  /**
   * An event belongs to the open profile. The API stamps every explorer event
   * with the job's `profileId` (null for a profile-less selection), and this
   * screen always has a profile, so a missing or other id is another
   * selection's job on this tab (Codex r1).
   */
  private forThisSelection(payload: DataMapperTabEventPayload): boolean {
    return this.profileId !== null && sameId(payload.profileId, this.profileId);
  }

  onProfilerEvent(event: ProfilerEvent, payload: DataMapperTabEventPayload) {
    if (!this.forThisSelection(payload)) return;
    this.profilerEventSeq++;
    if (event === DM_EVENTS.progress) {
      this.profileJob = {
        status: "running",
        kind: typeof payload.kind === "string" ? payload.kind : this.profileJob.kind,
        progress: payload as unknown as JobProgress,
        error: null,
      };
    } else if (event === DM_EVENTS.profileComplete) {
      this.profileJob = { ...this.profileJob, status: "completed", error: null };
      void this.loadProfiles();
    } else if (event === DM_EVENTS.profileError) {
      this.profileJob = {
        ...this.profileJob,
        status: "error",
        error: typeof payload.error === "string" ? payload.error : "Profiling failed.",
      };
    }
  }

  onAnalysisEvent(event: AnalysisEvent, payload: DataMapperTabEventPayload) {
    if (!this.forThisSelection(payload)) return;
    this.analysisEventSeq++;
    if (event === DM_EVENTS.progress) {
      // The analysis profiles every table first; that progress is the analysis job's.
      this.analysisJob = {
        status: "running",
        kind: "full-analysis",
        progress: payload as unknown as JobProgress,
        error: null,
      };
    } else if (event === DM_EVENTS.analysisComplete) {
      this.analysisJob = { ...this.analysisJob, status: "completed", error: null };
      void this.loadAnalysisResult();
      void this.loadProfiles();
    } else if (event === DM_EVENTS.analysisError) {
      this.analysisJob = {
        ...this.analysisJob,
        status: "error",
        error: typeof payload.error === "string" ? payload.error : "The analysis failed.",
      };
    }
  }

  // ---- Profiling ---------------------------------------------------------------------

  /** Profile one table (default: the selected one), in the background. */
  async profileTable(table: string | null = this.selectedTable): Promise<void> {
    if (!table) return;
    await this.startProfiling({ action: "profile-table", table }, "profile-table");
  }

  /** Profile every table, in the background. */
  async profileAll(): Promise<void> {
    await this.startProfiling({ action: "profile-all" }, "profile-all");
  }

  private async startProfiling(body: Record<string, unknown>, kind: string): Promise<void> {
    const service = dataPlaneService(this.rootStore, "data-mapper-explorer-profiler");
    const selection = this.selection;
    if (!service || !selection?.hasProfile || this.jobRunning) return;
    const epoch = this.epoch;
    const ended = sessionMark(this.rootStore);
    this.profileJob = { status: "running", kind, progress: null, error: null };
    try {
      await service.create({ ...body, ...selection.dataPlaneQuery });
    } catch (err) {
      if (isSessionEnded(err)) return;
      runInAction(() => {
        if (epoch !== this.epoch || ended()) return;
        if (NOT_CONNECTED_REASONS.has(refusalReason(err) ?? "")) this.notConnected = true;
        this.profileJob = {
          status: "error",
          kind,
          progress: null,
          error: explorerErrorMessage(err, "Profiling could not be started."),
        };
      });
    }
  }

  /** Every profile the server holds for this tab, following the byte pages. */
  async loadProfiles(): Promise<void> {
    const service = dataPlaneService(this.rootStore, "data-mapper-explorer-profiler");
    const selection = this.selection;
    if (!service || !selection?.hasProfile) return;
    const query = { ...selection.dataPlaneQuery };
    const seq = ++this.profilesSeq;
    const ended = sessionMark(this.rootStore);
    const current = () => seq === this.profilesSeq && !ended();
    try {
      const all: TableProfile[] = [];
      let skip: number | null = 0;
      for (let page = 0; skip !== null && page < MAX_PAGES; page++) {
        if (!current()) return;
        const answer = (await service.create({ action: "result", ...query, $skip: skip })) as PageAnswer<TableProfile>;
        if (!answer?.found) break;
        all.push(...(answer.rows ?? []));
        skip = typeof answer.nextCursor === "number" && answer.nextCursor > skip ? answer.nextCursor : null;
      }
      runInAction(() => {
        if (!current()) return;
        const next: Record<string, TableProfile> = { ...this.profiles };
        for (const p of all) next[p.fullName] = p;
        this.profiles = next;
      });
    } catch (err) {
      runInAction(() => {
        if (!current()) return;
        if (!isSessionEnded(err)) this.profileJob = { ...this.profileJob, error: loadErrorMessage(err, "The profiles could not be read.") };
      });
    }
  }

  // ---- AI analysis --------------------------------------------------------------------

  private async analyse<T>(
    kind: NonNullable<DataMapperExplorerStore["analysisBusy"]>,
    body: Record<string, unknown>,
    apply: (answer: T) => void
  ): Promise<boolean> {
    const service = dataPlaneService(this.rootStore, "data-mapper-explorer-analysis");
    const selection = this.selection;
    if (!service || !selection?.hasProfile || this.analysisBusy) return false;
    const epoch = this.epoch;
    const ended = sessionMark(this.rootStore);
    this.analysisBusy = kind;
    this.analysisError = null;
    try {
      const answer = (await service.create({ ...body, ...selection.dataPlaneQuery })) as T;
      runInAction(() => {
        if (epoch !== this.epoch || ended()) return;
        this.llmUnavailable = false;
        apply(answer);
      });
    } catch (err) {
      if (isSessionEnded(err)) return false;
      runInAction(() => {
        if (epoch !== this.epoch || ended()) return;
        // The router not being wired is said once, by the page's LLM notice.
        if (refusalReason(err) === "llm-unavailable") this.llmUnavailable = true;
        else this.analysisError = explorerErrorMessage(err, "The analysis failed.");
      });
    } finally {
      runInAction(() => {
        if (epoch === this.epoch && !ended()) this.analysisBusy = null;
      });
    }
    // False when the route was left or the session ended meanwhile: the caller's
    // follow-up reads (a reset page has no profiles, so they would refill it) are
    // then skipped.
    return epoch === this.epoch && !ended();
  }

  async classifyTable(table: string | null = this.selectedTable): Promise<void> {
    if (!table) return;
    const live = await this.analyse<TableClassification>("classify", { action: "classify-table", table }, (answer) => {
      this.classifications = { ...this.classifications, [table]: answer };
    });
    // Classifying profiles the table first when it had no profile.
    if (live && !this.profiles[table]) await this.loadProfiles();
  }

  async assessQuality(table: string | null = this.selectedTable): Promise<void> {
    if (!table) return;
    const live = await this.analyse<DataQualityAssessment>("quality", { action: "assess-quality", table }, (answer) => {
      this.qualities = { ...this.qualities, [table]: answer };
    });
    if (live && !this.profiles[table]) await this.loadProfiles();
  }

  async inferRelationships(): Promise<void> {
    await this.analyse<{ relationships?: InferredRelationship[] }>(
      "relationships",
      { action: "infer-relationships" },
      (answer) => {
        this.relationships = answer?.relationships ?? [];
      }
    );
  }

  /** Profile, classify and assess every table, infer relationships and summarise: in the background. */
  async startFullAnalysis(): Promise<void> {
    const service = dataPlaneService(this.rootStore, "data-mapper-explorer-analysis");
    const selection = this.selection;
    if (!service || !selection?.hasProfile || this.jobRunning) return;
    const epoch = this.epoch;
    const ended = sessionMark(this.rootStore);
    this.analysisError = null;
    this.analysisJob = { status: "running", kind: "full-analysis", progress: null, error: null };
    try {
      await service.create({ action: "full-analysis", ...selection.dataPlaneQuery });
      runInAction(() => {
        if (epoch === this.epoch && !ended()) this.llmUnavailable = false;
      });
    } catch (err) {
      if (isSessionEnded(err)) return;
      runInAction(() => {
        if (epoch !== this.epoch || ended()) return;
        if (refusalReason(err) === "llm-unavailable") {
          this.llmUnavailable = true;
          this.analysisJob = { ...IDLE_JOB };
          return;
        }
        this.analysisJob = {
          status: "error",
          kind: "full-analysis",
          progress: null,
          error: explorerErrorMessage(err, "The analysis could not be started."),
        };
      });
    }
  }

  /** The full analysis' result: the per-table analyses are paged, the rest rides on every page. */
  async loadAnalysisResult(): Promise<void> {
    const service = dataPlaneService(this.rootStore, "data-mapper-explorer-analysis");
    const selection = this.selection;
    if (!service || !selection?.hasProfile) return;
    const query = { ...selection.dataPlaneQuery };
    const seq = ++this.analysisSeq;
    const ended = sessionMark(this.rootStore);
    const current = () => seq === this.analysisSeq && !ended();
    try {
      let result: DatabaseAnalysis | null = null;
      let skip: number | null = 0;
      for (let page = 0; skip !== null && page < MAX_PAGES; page++) {
        if (!current()) return;
        const answer = (await service.create({ action: "result", ...query, $skip: skip })) as {
          found?: boolean;
          result?: DatabaseAnalysis;
          nextCursor?: number | null;
        };
        if (!answer?.found || !answer.result) break;
        result = result
          ? { ...answer.result, tables: [...result.tables, ...answer.result.tables] }
          : answer.result;
        skip = typeof answer.nextCursor === "number" && answer.nextCursor > skip ? answer.nextCursor : null;
      }
      runInAction(() => {
        if (!current()) return;
        this.fullAnalysis = result;
        if (result) this.relationships = result.inferredRelationships;
      });
    } catch (err) {
      runInAction(() => {
        if (!current()) return;
        if (!isSessionEnded(err)) this.analysisError = loadErrorMessage(err, "The analysis result could not be read.");
      });
    }
  }

  // ---- Reports ------------------------------------------------------------------------

  private get reportsService() {
    return dataPlaneService(this.rootStore, "data-mapper-explorer-reports");
  }

  async loadReports(): Promise<void> {
    const service = this.reportsService;
    const selection = this.selection;
    if (!service || !selection?.hasProfile) return;
    const query = { ...selection.selectionQuery };
    const seq = ++this.reportsSeq;
    const ended = sessionMark(this.rootStore);
    const current = () => seq === this.reportsSeq && !ended();
    this.reportsLoading = true;
    this.reportsError = null;
    try {
      const listing = (await service.find({ query })) as ExplorerReportListing[];
      runInAction(() => {
        if (!current()) return;
        this.reports = Array.isArray(listing) ? listing : [];
        const opened = this.openedReport;
        if (opened && !this.reports.some((g) => g.reports.some((r) => sameId(r.id, opened.id)))) {
          this.openedReport = null;
        }
      });
    } catch (err) {
      runInAction(() => {
        if (current()) { if (!isSessionEnded(err)) this.reportsError = loadErrorMessage(err, "The saved reports could not be loaded."); }
      });
    } finally {
      runInAction(() => {
        if (current()) this.reportsLoading = false;
      });
    }
  }

  setReportScope(scope: DataMapperScope) {
    this.reportScope = scope;
  }

  /** What a saved report holds: the source's name, its profiles and the analysis, as assembled here. */
  get reportBody(): Record<string, unknown> {
    const analysis: Record<string, unknown> = this.fullAnalysis
      ? { ...this.fullAnalysis }
      : {
          tables: Object.keys({ ...this.classifications, ...this.qualities }).flatMap((tableName) => {
            const classification = this.classifications[tableName];
            const qualityAssessment = this.qualities[tableName];
            return classification || qualityAssessment ? [{ tableName, classification, qualityAssessment }] : [];
          }),
          inferredRelationships: this.relationships ?? [],
        };
    return {
      database: this.sourceName || "unknown",
      generatedAt: new Date().toISOString(),
      tables: this.tables.length,
      analysis,
      profiles: Object.values(this.profiles),
    };
  }

  /** Save the current profiles and analysis as a report in `reportScope`. */
  async saveReport(): Promise<boolean> {
    const service = this.reportsService;
    const selection = this.selection;
    if (!service || !selection?.hasProfile || !this.canSaveReport) return false;
    const epoch = this.epoch;
    const ended = sessionMark(this.rootStore);
    const live = () => epoch === this.epoch && !ended();
    this.reportBusy = "save";
    this.reportsError = null;
    this.reportNotice = null;
    try {
      const saved = (await service.create({
        report: this.reportBody,
        scope: this.reportScope,
        ...selection.selectionQuery,
      })) as ExplorerReportMeta | null;
      runInAction(() => {
        if (!live()) return;
        this.reportNotice = `Saved the report for ${saved?.database ?? "this source"}.`;
      });
      // The page was left (or the session ended) while the save ran: the reset
      // emptied the list, and reloading it here would refill it.
      if (live()) await this.loadReports();
      return true;
    } catch (err) {
      await this.reportFailure(live, err, "The report could not be saved.");
      return false;
    } finally {
      runInAction(() => {
        if (live()) this.reportBusy = null;
      });
    }
  }

  async openReport(id: string): Promise<void> {
    const service = this.reportsService;
    const selection = this.selection;
    if (!service || !selection?.hasProfile || this.reportBusy) return;
    const epoch = this.epoch;
    const ended = sessionMark(this.rootStore);
    const live = () => epoch === this.epoch && !ended();
    this.reportBusy = "open";
    this.reportsError = null;
    try {
      const report = (await service.get(id, { query: { ...selection.selectionQuery } })) as ExplorerReport;
      runInAction(() => {
        if (live()) this.openedReport = report;
      });
    } catch (err) {
      await this.reportFailure(live, err, "The report could not be opened.");
    } finally {
      runInAction(() => {
        if (live()) this.reportBusy = null;
      });
    }
  }

  closeReport() {
    this.openedReport = null;
  }

  async deleteReport(id: string): Promise<boolean> {
    const service = this.reportsService;
    const selection = this.selection;
    if (!service?.remove || !selection?.hasProfile || this.reportBusy) return false;
    const epoch = this.epoch;
    const ended = sessionMark(this.rootStore);
    const live = () => epoch === this.epoch && !ended();
    this.reportBusy = "delete";
    this.reportsError = null;
    this.reportNotice = null;
    try {
      await service.remove(id, { query: { ...selection.selectionQuery } });
      runInAction(() => {
        if (!live()) return;
        if (this.openedReport && sameId(this.openedReport.id, id)) this.openedReport = null;
        this.reportNotice = "The report was deleted.";
      });
      if (live()) await this.loadReports();
      return true;
    } catch (err) {
      await this.reportFailure(live, err, "The report could not be deleted.");
      return false;
    } finally {
      runInAction(() => {
        if (live()) this.reportBusy = null;
      });
    }
  }

  /** A report call failed: a 409 or 404 means the list is stale, so it is reloaded and said so. */
  private async reportFailure(live: () => boolean, err: unknown, fallback: string): Promise<void> {
    // A sign-out ended the call: it says nothing about the report, and the
    // "Signed out before … finished" sentence belongs to no one's page.
    if (isSessionEnded(err)) return;
    const stale = isCode(err, 409) ? REPORT_CONFLICT_MESSAGE : isCode(err, 404) ? REPORT_GONE_MESSAGE : null;
    runInAction(() => {
      if (!live()) return;
      if (stale) {
        this.reportNotice = stale;
        this.openedReport = null;
      } else {
        this.reportsError = loadErrorMessage(err, fallback);
      }
    });
    if (stale && live()) await this.loadReports();
  }

  clearReportNotice() {
    this.reportNotice = null;
  }

  clearAnalysisError() {
    this.analysisError = null;
  }

  // ---- Navigation ------------------------------------------------------------------------

  goToSource() {
    void this.rootStore.routerStore?.goTo(DATA_MAPPER_SOURCE_ROUTE);
  }

  goToLlmRouting() {
    void this.rootStore.routerStore?.goTo(LLM_ROUTING_ROUTE);
  }

  /** Route exit: stop listening and drop everything read from the source. */
  reset() {
    this.disposeEvents?.();
    this.disposeEvents = null;
    this.epoch++;
    this.loadSeq++;
    this.tablesSeq++;
    this.profilesSeq++;
    this.analysisSeq++;
    this.reportsSeq++;
    // A report's audience is chosen per visit: never carried to another profile (Codex r1).
    this.reportScope = "profile";
    this.tables = [];
    this.tablesLoading = false;
    this.tablesError = null;
    this.tablesCut = false;
    this.notConnected = false;
    this.sourceName = null;
    this.tableSearch = "";
    this.selectedTable = null;
    this.profiles = {};
    this.profileJob = { ...IDLE_JOB };
    this.classifications = {};
    this.qualities = {};
    this.relationships = null;
    this.fullAnalysis = null;
    this.analysisJob = { ...IDLE_JOB };
    this.analysisBusy = null;
    this.analysisError = null;
    this.llmUnavailable = false;
    this.reports = [];
    this.reportsLoading = false;
    this.reportsError = null;
    this.reportNotice = null;
    this.reportBusy = null;
    this.openedReport = null;
  }
}

export default DataMapperExplorerStore;
